/** Participant profile across platforms (§12.5 one profile, §16.3 masked by default, §17.4 signals for review). */
import { useQuery } from '@tanstack/react-query';
import { Button, useIntl } from '@windrop/ui';
import { useParams } from 'react-router';
import { ErrorState } from '../../components/EmptyState';
import { MaskedCell } from '../../components/MaskedCell';
import { PageHeader } from '../../components/PageHeader';
import { ToneBadge } from '../../components/StatusBadge';
import { isApiError } from '../../lib/api/client';
import { entriesApi } from '../../lib/api/entries';

export function ParticipantPage() {
  const { id = '' } = useParams();
  const intl = useIntl();
  const q = useQuery({
    queryKey: ['participant', id],
    queryFn: () => entriesApi.participant(id),
    enabled: Boolean(id),
  });
  if (q.isError)
    return (
      <ErrorState
        title="Participant not found"
        body="It may belong to another tenant or the id is wrong."
        reference={isApiError(q.error) ? q.error.supportReference : undefined}
        action={<Button href="/lotteries">Back to lotteries</Button>}
      />
    );
  const p = q.data;
  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Lotteries', to: '/lotteries' }, { label: 'Participant' }]}
        title={p?.displayName ?? 'Participant'}
        description={
          p
            ? `Profile created ${intl.formatDateTime(p.createdAt)} · arrived from ${p.sourcePlatform ?? 'direct'}`
            : undefined
        }
      />
      {p ? (
        <div className="wdc-grid" data-cols="2" data-testid="participant-profile">
          <section className="wdc-panel">
            <h2>Channels</h2>
            <ul>
              {p.channels.map((c) => (
                <li key={c.channelId}>
                  {c.platform}:{' '}
                  <MaskedCell
                    masked={c.valueMasked}
                    subject={{
                      kind: 'PARTICIPANT_CONTACT',
                      participantId: p.participantId,
                      channelId: c.channelId,
                    }}
                    what={`${c.platform} contact`}
                  />
                  {c.primary ? ' (primary)' : ''} · linked {intl.formatDateTime(c.linkedAt)}
                </li>
              ))}
            </ul>
          </section>
          <section className="wdc-panel">
            <h2>Activity</h2>
            <dl className="wdc-facts">
              <div>
                <dt>Lotteries entered</dt>
                <dd>{p.lotteriesEntered}</dd>
              </div>
              <div>
                <dt>Prizes won</dt>
                <dd>{p.prizesWon}</dd>
              </div>
            </dl>
          </section>
          <section className="wdc-panel">
            <h2>Consents (§12.5, separate purposes)</h2>
            <dl className="wdc-facts" data-testid="participant-consents">
              <div>
                <dt>Sponsor communication</dt>
                <dd>{p.consents.sponsorCommunication ? 'Granted' : 'Not granted'}</dd>
              </div>
              <div>
                <dt>Marketing</dt>
                <dd>
                  {p.consents.marketing
                    ? `Granted (text v${p.consents.marketingTextVersion ?? '?'}, ${p.consents.marketingRecordedAt ? intl.formatDateTime(p.consents.marketingRecordedAt) : ''})`
                    : 'Not granted'}
                  {p.consents.marketingWithdrawnAt
                    ? ` · withdrawn ${intl.formatDateTime(p.consents.marketingWithdrawnAt)}`
                    : ''}
                </dd>
              </div>
              <div>
                <dt>Winner publicity</dt>
                <dd>{p.consents.winnerPublicity ? 'Granted (initials only)' : 'Not granted'}</dd>
              </div>
            </dl>
          </section>
          <section className="wdc-panel">
            <h2>Risk signals</h2>
            <p className="wd-muted">
              Signals are reasons for review, never standalone guilt (§17.4). Exclusion needs a documented
              eligibility review.
            </p>
            {p.riskFlags.length ? (
              <ul>
                {p.riskFlags.map((f, i) => (
                  <li key={i}>
                    <ToneBadge tone={f.reviewed ? 'neutral' : 'warning'}>{f.code}</ToneBadge>{' '}
                    {intl.formatDateTime(f.at)} {f.reviewed ? '· reviewed' : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="wd-muted">No signals recorded.</p>
            )}
          </section>
        </div>
      ) : null}
    </>
  );
}
